import { useContext, useEffect } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { fg } from '@confluence/feature-gating';
import { SPAViewContext } from '@confluence/spa-view-context/entry-points/SPAViewContext.ts';

// TAP trait name and values
const LIVE_PAGES_BETA_TRAIT_NAME = 'confluence_live_pages_beta';
const LIVE_PAGES_BETA_DEFAULT_ON_VALUE = 'beta-default-on';
const LIVE_PAGES_BETA_DEFAULT_OFF_VALUE = 'beta-default-off';
export const LIVE_DOC_GA_EXP_TRAIT_NAME = 'confluence_modernize';
const LIVE_DOC_GA_EXP_TEST_GROUP_VALUE = 'exp-test';
export const LIVE_DOC_GA_EXP_CONTROL_GROUP_VALUE = 'exp-control';

export enum LivePageReleasePhase {
	NOT_ENABLED = 'not_enabled',
	OPEN_BETA_DEFAULT_ON = 'open_beta_default_on', // Tenant is in open beta and have opted into the open beta experience. Live docs are available and turned on by default is most space settings.
	OPEN_BETA_DEFAULT_OFF = 'open_beta_default_off', // Tenant is in open beta, and have opted out of the open beta experience. Live docs are still available, but will no longer be turned on by default in space settings.
	MODERNIZATION_GA_EXP_TEST = 'modernization_ga_exp_test', // Tenant is enrolled into the Modernization GA experiment test group. Live docs are available.
	MODERNIZATION_GA_EXP_CONTROL = 'modernization_ga_exp_control', // Tenant is enrolled into the Modernization GA experiment control group. Live docs are NOT available.
}

const RELEASE_PHASES_WITH_FEATURE_ENABLED = [
	LivePageReleasePhase.OPEN_BETA_DEFAULT_ON,
	LivePageReleasePhase.OPEN_BETA_DEFAULT_OFF,
	LivePageReleasePhase.MODERNIZATION_GA_EXP_TEST,
];

const useLivePageReleasePhase = (): LivePageReleasePhase => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { experimentFeatures, loading: isSPAViewContextLoading } = useContext(SPAViewContext);

	useEffect(() => {
		if (isSPAViewContextLoading) {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					action: 'traitValueLoading',
					actionSubject: 'useLivePageReleasePhase',
					source: 'useLivePageReleasePhase',
				},
			}).fire();
		}
	}, [createAnalyticsEvent, isSPAViewContextLoading]);

	let openBetaTraitValue;
	let modernizationGaTraitValue;
	try {
		const parsedExperimentFeatures = JSON.parse(experimentFeatures);
		if (typeof parsedExperimentFeatures === 'object') {
			({
				[LIVE_PAGES_BETA_TRAIT_NAME]: openBetaTraitValue,
				[LIVE_DOC_GA_EXP_TRAIT_NAME]: modernizationGaTraitValue,
			} = parsedExperimentFeatures);
		}
	} catch (e) {
		openBetaTraitValue = null;
		modernizationGaTraitValue = null;
	}

	// Modernization GA release phase
	/**
	 * Tenants are enrolled once they land in confluence.
	 *
	 * Tenants already in open beta or release tracks will not be part of the GA experiment.
	 * All other tenants will be split 50/50 between the test and control groups.
	 *
	 * Only the test group will have the modernization ga experience.
	 */
	if (modernizationGaTraitValue === LIVE_DOC_GA_EXP_TEST_GROUP_VALUE) {
		return LivePageReleasePhase.MODERNIZATION_GA_EXP_TEST;
	} else if (modernizationGaTraitValue === LIVE_DOC_GA_EXP_CONTROL_GROUP_VALUE) {
		return LivePageReleasePhase.MODERNIZATION_GA_EXP_CONTROL;
	}

	// Open Beta release phase
	/**
	 * Open Beta is now closed and users are no longer able to opt in. Open Beta is determine by the TAP trait value.
	 * Any tenant that has opted in at any point will be part of the GA experience. However, they will not be part of the GA experiment.
	 *
	 * https://hello.atlassian.net/wiki/spaces/PCG/pages/4415395121/Live+Pages+Open+Beta+Feature+Gate+Approach
	 */
	if (
		openBetaTraitValue === LIVE_PAGES_BETA_DEFAULT_ON_VALUE ||
		fg('confluence_live_pages_open_beta_trait_opted_in') // we can assume that the tenant has opted in if the gate is true, opt out is not possible at the moment.
	) {
		return LivePageReleasePhase.OPEN_BETA_DEFAULT_ON;
	} else if (openBetaTraitValue === LIVE_PAGES_BETA_DEFAULT_OFF_VALUE) {
		return LivePageReleasePhase.OPEN_BETA_DEFAULT_OFF;
	}

	// Default
	return LivePageReleasePhase.NOT_ENABLED;
};

/**
 * Returns if the current tenant has the ability to use Live Pages.
 * This takes into account multiple releases that contribute to the feature's availability.
 */
export const useIsLivePagesFeatureEnabled = (): {
	isLivePagesFeatureEnabled: boolean;
	livePageReleasePhase: LivePageReleasePhase;
} => {
	const phase = useLivePageReleasePhase();
	return {
		isLivePagesFeatureEnabled: RELEASE_PHASES_WITH_FEATURE_ENABLED.includes(phase),
		livePageReleasePhase: phase,
	};
};
